import { action }                      from 'mobx';
import { makeObservable }              from 'mobx';
import { observable }                  from 'mobx';
import { Shape }                       from '@tokamap/core';
import { TokaMapInstance }             from '@tokamap/core';
import { LngLat }                      from '@tokamap/core';
import MapModel                        from '@models/map/MapModel';
import { extractRawId }                from '@tokamap/core/loaders/formats/shared';
import leftSidebarStore                from '@/TokamapEditor/stores/leftSidebarStore';
import type { HighlightShapesOptions } from '@tokamap/core';
import EditorUserStore                 from '@/TokamapEditor/stores/EditorUserStore';
import { ZONE_SIDEBAR_WIDTH }          from '@/TokamapEditor/ux/constants';
import { declaredMapKind }             from '@/TokamapEditor/helpers/mapKind';
import { lengthUnitOf }                from '@/TokamapEditor/helpers/mapKind';
import { gridStepOf }                  from '@/TokamapEditor/helpers/mapKind';
import { rulersOf }                    from '@/TokamapEditor/helpers/mapKind';
import type { MapKind }                from '@/TokamapEditor/helpers/mapKind';
import type { LengthUnit }             from '@/TokamapEditor/helpers/mapKind';

class CurrentEditorStore {

	editorUserStore: EditorUserStore;

	private _map: MapModel | undefined;

	@observable _isCenterDefined = false;

	@observable mapKind: MapKind | null = null;

	@observable lengthUnit: LengthUnit = 'm';

	@observable gridStep: number | null = null;

	@observable displayedGridStep: number | null = null;

	@observable rulers = true;

	@observable _tokamap?: TokaMapInstance;

	@observable _selectedShape: Shape | null = null;

	@observable _isDrawing = false;

	// Formes résolues de la sélection rectangle (Ctrl+drag, BESOINS.md §13) — vide hors sélection
	// multiple. Tenu à jour par `index.tsx` en miroir de `_selectedShape` (même mécanisme :
	// `@tokamap/core` n'expose que des ids, la résolution en `Shape[]` vit côté consommateur).
	@observable.ref _selectionShapes: Shape[] = [];

	@observable freshShapeId: string | null = null;
	/** Forme de décor dont on a ouvert « En faire un point d'intérêt » : comme une forme tout juste
	 * tracée, son panneau n'affiche que la question, sans les réglages en dessous. */
	@observable promotingShapeId: string | null = null;

	@observable requestedShapeId: string | null = null;

	// Correspondance entre l'id de la forme côté carte (`event.shape.id`) et l'id de la feature côté API
	// (`apiFeature.id`), reçu au retour du `patch` dans `onSave`.
	shapeIdToFeatureId: Record<string, string> = {};

	documentOrigin: { center: LngLat; zoom: number; zoomMax?: number; rotationDeg: number } | null = null;

	// Hors mobx : purement un passage de consigne d'un appel au rendu suivant, aucun affichage n'en
	// dépend directement.
	private _skipNextSelectionSidebar = false;

	constructor() {
		this.editorUserStore = new EditorUserStore();

		makeObservable(this);
	}

	get get() {
		return this._tokamap!;
	}

	/**
	 * Poussé par l'abonnement à `shapeStore.onChange` (voir `TokamapEditor/index.tsx`).
	 * `@tokamap/core` n'est pas mobx : `getAllShapes()` dans un `observer` ne crée aucune dépendance,
	 * ce compteur fait le lien.
	 */
	@observable shapesRevision = 0;

	@action bumpShapesRevision() {
		this.shapesRevision++;
	}

	/** À préférer à `get.getAllShapes()` dans un `observer`, qui ne se re-rendrait pas. Vide tant que
	 * `@tokamap/core` n'a pas rendu la carte. */
	get allShapes(): Shape[] {
		// Lecture volontaire : c'est elle qui abonne l'appelant.
		void this.shapesRevision;

		return this._tokamap?.getAllShapes() || [];
	}

	get map() {
		return this._map;
	}

	@action set(store: TokaMapInstance) {
		this._tokamap = store;
		this._isDrawing = false;
		store.mapAdapter.setMeasureUnit(this.lengthUnit);

		this.unsubscribeSaveStatus?.();
		this.unsubscribeSaveStatus = store.onSaveStatusChange(event => {
			if (event.status === 'pending') {
				this.lastLocalSaveAt.set(event.shapeId, Infinity);
			} else {
				this.lastLocalSaveAt.set(event.shapeId, Date.now());
			}
		});

		return store;
	}

	/** Fin du dernier enregistrement local de chaque forme (`Infinity` tant qu'il est en cours). Pas
	 * observable : seul `EditorMercureListener` le lit, au fil des évènements. */
	private lastLocalSaveAt = new Map<string, number>();
	private unsubscribeSaveStatus?: () => void;

	/**
	 * Vrai pendant qu'une forme a un enregistrement en vol, et un court instant après : un écho Mercure
	 * reçu à ce moment est celui de nos propres modifications. Plusieurs enregistrements partent coup
	 * sur coup (une liaison en enchaîne une demi-douzaine) et l'écho d'un ancien peut arriver après
	 * l'état local plus récent : le rejouer effacerait ce qui vient d'être posé (l'image, par exemple).
	 */
	isLocallySaving(shapeId: string, graceMs = 1500) {
		const at = this.lastLocalSaveAt.get(shapeId);

		return at !== undefined && Date.now() - at < graceMs;
	}

	get isDrawing() {
		return this._isDrawing;
	}

	@action setIsDrawing(value: boolean) {
		this._isDrawing = value;
	}

	get selectedShape() {
		return this._selectedShape;
	}

	@action setSelectedShape(shape: Shape | null) {
		this._selectedShape = shape;
		this.get?.mapAdapter.setFocusedShape(shape?.id ?? null);
		return shape;
	}

	@action setSelectionShapes(shapes: Shape[]) {
		this._selectionShapes = shapes;
	}

	@action markFreshShape(shapeId: string | null) {
		this.freshShapeId = shapeId;
	}

	@action setPromotingShape(shapeId: string | null) {
		this.promotingShapeId = shapeId;
	}

	@action requestShapeSelection(shapeId: string) {
		this.requestedShapeId = shapeId;
	}

	@action takeRequestedShape(): Shape | null {
		const shapeId = this.requestedShapeId;

		if (!shapeId || !this._tokamap) return null;

		const shape = this._tokamap.getAllShapes().find(s => s.id === shapeId);

		if (!shape) return null;

		this.requestedShapeId = null;

		return shape;
	}

	// Désélectionne la forme unique (fermeture de la sidebar sans clic sur la carte, BESOINS.md §4) :
	// `removeFromSelection` sur la dernière forme d'une sélection rectangle appelle en interne
	// `clearSelection` côté `@tokamap/core`, qui émet `selection:changed: []` — capté par
	// `index.tsx` pour remettre à jour `selectionShapeIds` local.
	//
	// Une sélection rectangle MULTIPLE (BESOINS.md §13) n'est volontairement PAS touchée ici : fermer
	// la sidebar de forme (croix, Échap) ne doit annuler que ce que le clic simple a sélectionné, pas
	// le geste de sélection multiple lui-même — voir feedback utilisateur sur ce point précis. Le
	// rectangle reste désélectionnable par ses propres mécanismes (clic en dehors, Échap, Suppr —
	// `MapLibreAdapter`), pas par la fermeture de la sidebar qui l'affichait.
	@action clearSelection() {
		if (this.isMultiSelection) {
			this.setSelectedShape(null);
			return;
		}
		if (this._selectionShapes.length) {
			[...this._selectionShapes].forEach(shape => this.get?.mapAdapter.removeFromSelection(shape.id));
		}
		this.setSelectedShape(null);
	}

	// Formes que la sidebar doit éditer : la sélection rectangle prime sur le clic simple — même
	// priorité que celle déjà établie dans `useKeyboardShortcuts.ts` pour Suppr/Ctrl+C/Ctrl+L.
	get sidebarShapes(): Shape[] {
		if (this._selectionShapes.length > 1) return this._selectionShapes;
		return this._selectedShape ? [this._selectedShape] : [];
	}

	get isMultiSelection(): boolean {
		return this._selectionShapes.length > 1;
	}

	@action setMap(map: MapModel) {
		this._map = map;
		this.editorUserStore.restart();
	}

	get isCenterDefined() {
		return this._isCenterDefined;
	}

	get isFreePlan() {
		return this.mapKind === 'plan';
	}

	get showsRulers() {
		return this.isFreePlan && this.rulers;
	}

	@action readMapKindFrom(configuration: { kind?: unknown; unit?: unknown; grid?: unknown; rulers?: unknown; } | null | undefined) {
		this.mapKind = declaredMapKind(configuration);
		this.gridStep = gridStepOf(configuration);
		this.rulers = rulersOf(configuration);
		this.setLengthUnit(lengthUnitOf(configuration));
	}

	@action setDisplayedGridStep(step: number | null) {
		if (this.displayedGridStep !== step) this.displayedGridStep = step;
	}

	@action setGridStep(step: number | null) {
		this.gridStep = step;
	}

	@action setRulers(visible: boolean) {
		this.rulers = visible;
	}

	saveMapKindSettings() {
		this.get.setBackgroundColor(this.get.getBackgroundColor(), { skipHistory: true });
	}

	@action setLengthUnit(unit: LengthUnit) {
		this.lengthUnit = unit;
		this._tokamap?.mapAdapter.setMeasureUnit(unit);
	}

	get mapKindConfiguration() {
		return this.mapKind ? { kind: this.mapKind, unit: this.lengthUnit, grid: this.gridStep ? { step: this.gridStep } : null, rulers: this.rulers } : {};
	}

	@action setIsCenterDefined(value = true) {
		this._isCenterDefined = value;
	}

	getFeatureIdFromShapeId(shapeId: string | number) {
		return this.shapeIdToFeatureId[shapeId] || shapeId;
	}

	getShapeIdFromFeatureId(featureId: number) {
		return Object.entries(this.shapeIdToFeatureId).find((arr) => {
			return Number(arr.at(1)) === featureId;
		})?.at(0) || featureId;
	}

	findShapeFromFeatureId(featureId: number) {
		const shapes = this.get.getAllShapes();

		return shapes.find(shape => {
			const rawId = extractRawId(shape);
			return featureId === Number(this.getFeatureIdFromShapeId(rawId));
		});
	}

	/**
	 * Met en évidence un ensemble de formes ET les place en sélection multiple, pour qu'on puisse
	 * enchaîner sur une action de groupe — appliquer un thème, changer un style.
	 *
	 * `selectShapes` est l'API que `@tokamap/core` expose pour composer une sélection sans geste de
	 * l'utilisateur ; elle remplace la sélection courante, ce qui est le comportement voulu ici
	 * puisque l'appelant part d'un ensemble qu'il a calculé lui-même.
	 */
	highlightAndSelectShapes(shapeIds: string[]) {
		if (!shapeIds.length) return;

		// Une sélection multiple n'a de sens qu'à partir de deux formes : pour une seule, la composer
		// afficherait "1 forme sélectionnée" et mettrait l'éditeur dans un mode dont on n'a rien à
		// faire. Le surlignage et le zoom suffisent à montrer laquelle.
		const multiple = shapeIds.length > 1;

		// Consommé par l'effet de `TokamapEditor/index.tsx` : la sélection est composée d'ici, pas par
		// un geste sur le plan, et l'utilisateur est en train de regarder une liste — lui substituer la
		// sidebar de forme lui retirerait l'écran d'où il vient de cliquer.
		//
		// Posé seulement quand on compose vraiment une sélection : sans sélection, rien ne viendrait le
		// consommer et il ferait sauter la sidebar du PROCHAIN clic sur une forme.
		if (multiple) this._skipNextSelectionSidebar = true;

		this.setHighlightedShapes(shapeIds, { zoomToFit: true }, false);

		if (multiple) this.get?.mapAdapter.selectShapes(shapeIds);
	}

	/** Mise en évidence à appliquer une fois les formes de la carte `mapId` chargées. */
	private _pendingHighlight: { mapId: string; filter: (shape: Shape) => boolean } | null = null;

	requestHighlightOnMap(mapId: id, filter: (shape: Shape) => boolean) {
		this._pendingHighlight = { mapId: String(mapId), filter };
	}

	/** Les formes peuvent arriver après l'instance : la demande n'est retirée qu'une fois appliquée. */
	consumePendingHighlight() {
		const pending = this._pendingHighlight;

		if (!pending || !this._tokamap) return;

		if (String(this._map?.id) !== pending.mapId) return;

		const shapeIds = this._tokamap.getAllShapes().filter(pending.filter).map(shape => shape.id);

		if (!shapeIds.length) return;

		this._pendingHighlight = null;
		this.highlightAndSelectShapes(shapeIds);
	}

	/** `true` une seule fois, pour la sélection qui vient d'être composée par programme. */
	consumeSkipSelectionSidebar() {
		const skip = this._skipNextSelectionSidebar;

		this._skipNextSelectionSidebar = false;

		return skip;
	}

	setHighlightedShapes(shapeIds: string[], options?: HighlightShapesOptions, closeSidebar = true) {
		if (closeSidebar && shapeIds.length) leftSidebarStore.close();
		// Sidebar gardée ouverte mais son overlay sombre masquerait les formes qu'on vient de
		// surligner/zoomer dessus — même logique que `LeftSidebar`'s `mask` pendant un drag.
		else if (shapeIds.length) leftSidebarStore.setOption('overlay', false);

		this.get.highlightShapes(shapeIds, {
			...(() => {
				if (closeSidebar) return {};
				return { ensureVisible: { left: ZONE_SIDEBAR_WIDTH } };
			})(),
			...options,
		});
	}
}

export default new CurrentEditorStore();